import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';

export type MesStatus = 'vazio' | 'parcial' | 'concluido' | 'cliente';

export interface IContaCilMesStatus {
  mes: number; // 1..12
  total: number;
  conciliados: number;
  pendentes: number;
  enviados_cliente: number;
  status: MesStatus;
  empresas: number;
  empresas_ativas: number;
  empresas_inativas: number;
}

export interface IContaCilDashboardKpis {
  // Contagem CRUA (cada lançamento) — bate com a tela de Lançamentos.
  empresas_total: number;
  // Split ativa/inativa (inativa = dt_cli_ate preenchida = fim de cliente).
  empresas_ativas: number;
  empresas_inativas: number;
  lancamentos_total: number;
  conciliados: number;
  pendentes: number;
  enviados_cliente: number;
  percent_conciliado: number;
  // Contagem EXPORTÁVEL (partida-dobrada deduplicada) — bate com o Domínio.
  lancamentos_exportaveis: number;
  conciliados_exportaveis: number;
  pendentes_exportaveis: number;
  percent_conciliado_exportavel: number;
}

export interface IContaCilDashboardData {
  kpis: IContaCilDashboardKpis;
  meses_status: IContaCilMesStatus[];
  mes_detail: IContaCilMesStatus | null;
}

interface IContaCilDashboardParams {
  empresa_id?: number;
  ano: number;
  mes?: number;
}

// Backend envelopa a resposta em IGetResponseBase: { data, message, meta }.
interface IApiEnvelope<T> {
  data: T;
  message?: string;
  meta?: unknown;
}

const getContaCilDashboard = async (
  params: IContaCilDashboardParams,
): Promise<IContaCilDashboardData> => {
  const res = await api.get<IApiEnvelope<IContaCilDashboardData>>('/api/v1/contacil/dashboard/', {
    params,
  });
  // Desembrulha o envelope IGetResponseBase -> IContaCilDashboardData.
  // NOTA: o catch que mascarava erros foi removido — 401/403/500/timeout
  // agora propagam pro useQuery (isError dispara). Empty state real
  // (sem lancamentos no periodo) e detectado via data.kpis.lancamentos_total
  // === 0 no componente, nao confundido com falha.
  return res.data.data;
};

export const useContaCilDashboard = (params: IContaCilDashboardParams) => {
  return useQuery({
    queryKey: ['contacil', 'dashboard', params] as const,
    queryFn: () => getContaCilDashboard(params),
    staleTime: 5 * 60 * 1000,
  });
};

export interface IContaCilMesEmpresa {
  empresa_id: number;
  razao_social: string | null;
  total: number;
  conciliados: number;
  pendentes: number;
}

// Empresas com movimentação num mês (lista interativa do painel do calendário).
export const useContaCilMesEmpresas = (ano: number, mes: number | null) =>
  useQuery({
    queryKey: ['contacil', 'mes-empresas', ano, mes] as const,
    enabled: mes != null,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<IContaCilMesEmpresa[]> => {
      const res = await api.get<IApiEnvelope<IContaCilMesEmpresa[]>>(
        '/api/v1/contacil/dashboard/mes-empresas',
        { params: { ano, mes } },
      );
      return res.data.data;
    },
  });

export interface IContaCilMesDetalhePage {
  items: IContaCilMesEmpresa[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

// Detalhe do mês PAGINADO (cross-empresa) — usado pelo painel do hub.
// Ordena por pendentes desc no backend.
export const useContaCilMesDetalhe = (ano: number, mes: number | null, page: number, size = 20) =>
  useQuery({
    queryKey: ['contacil', 'mes-detalhe', ano, mes, page, size] as const,
    enabled: mes != null,
    staleTime: 60 * 1000,
    placeholderData: prev => prev, // mantém a página anterior enquanto carrega
    queryFn: async (): Promise<IContaCilMesDetalhePage> => {
      const res = await api.get<IApiEnvelope<IContaCilMesDetalhePage>>(
        '/api/v1/contacil/dashboard/mes-detalhe',
        { params: { ano, mes, page, size } },
      );
      return res.data.data;
    },
  });

export interface IContaCilOperKpi {
  valor: number;
  contexto: number | null;
  serie: number[];
}

export interface IContaCilOperacional {
  pendentes: IContaCilOperKpi;
  aguardando_exportacao: IContaCilOperKpi;
  exportado_pct: IContaCilOperKpi;
  extratos_nao_homologados: IContaCilOperKpi;
  aguardando_identificacao: IContaCilOperKpi;
  empresas_fora_operacao: IContaCilOperKpi;
}

// Os 6 KPIs operacionais do hub (pendências, export, identificação, empresas).
export const useContaCilOperacional = (ano: number) =>
  useQuery({
    queryKey: ['contacil', 'operacional', ano] as const,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<IContaCilOperacional> => {
      const res = await api.get<IApiEnvelope<IContaCilOperacional>>(
        '/api/v1/contacil/dashboard/operacional',
        { params: { ano } },
      );
      return res.data.data;
    },
  });

export interface IContaCilHomologacao {
  pdf_importados: number;
  extraidos: number;
  homologados: number;
}
export interface IContaCilEmpresaFora {
  empresa_id: number;
  razao_social: string | null;
  uf: string | null;
  contas: number;
}
export interface IContaCilResumo {
  homologacao: IContaCilHomologacao;
  emp_base: number;
  emp_operando: number;
  emp_fora: number;
  emp_fechadas: number;
  emp_andamento: number;
  fora_lista: IContaCilEmpresaFora[];
}

// Resumo: homologação PDF + donut de empresas + lista fora de operação.
export const useContaCilResumo = (ano: number, mes?: number | null) =>
  useQuery({
    queryKey: ['contacil', 'resumo', ano, mes ?? null] as const,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<IContaCilResumo> => {
      const res = await api.get<IApiEnvelope<IContaCilResumo>>(
        '/api/v1/contacil/dashboard/resumo',
        { params: mes ? { ano, mes } : { ano } },
      );
      return res.data.data;
    },
  });

export interface IContaCilFechMes {
  mes: number;
  pendentes: number | null; // null = mês futuro
}
export interface IContaCilFechAno {
  ano: number;
  meses: IContaCilFechMes[];
}
export interface IContaCilFechamentos {
  anos: IContaCilFechAno[];
  total: number;
  pico: number;
}

export type FechStatus = 'nao_conciliado' | 'a_exportar' | 'a_fechar';
export interface IContaCilFechEmpresa {
  empresa_id: number;
  razao_social: string | null;
  uf: string | null;
  lancamentos: number;
  conciliados: number;
  pendentes: number;
  exportados: number;
  percent_conciliado: number;
  status: FechStatus;
}
export interface IContaCilFechDetalhe {
  ano: number;
  mes: number;
  operaram: number;
  fechadas: number;
  pendentes: number;
  empresas: IContaCilFechEmpresa[];
}

// Drill-down: empresas pendentes de fechamento numa competência (pro modal).
export const useContaCilFechamentoDetalhe = (ano: number, mes: number | null) =>
  useQuery({
    queryKey: ['contacil', 'fechamento-detalhe', ano, mes] as const,
    enabled: mes != null,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<IContaCilFechDetalhe> => {
      const res = await api.get<IApiEnvelope<IContaCilFechDetalhe>>(
        '/api/v1/contacil/dashboard/fechamentos/detalhe',
        { params: { ano, mes } },
      );
      return res.data.data;
    },
  });

// Calendário de fechamentos contábeis (matriz ano × mês de empresas pendentes).
export const useContaCilFechamentos = (ate: number, anos: number) =>
  useQuery({
    queryKey: ['contacil', 'fechamentos', ate, anos] as const,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<IContaCilFechamentos> => {
      const res = await api.get<IApiEnvelope<IContaCilFechamentos>>(
        '/api/v1/contacil/dashboard/fechamentos',
        { params: { ate, anos } },
      );
      return res.data.data;
    },
  });

export interface IContaCilSaldoMes {
  periodo_ini: string | null;
  periodo_fim: string | null;
  saldo_inicial: number | null;
  saldo_final: number | null;
}

// Saldos (inicial/final) por statement de uma conta — pro card de saldo do Conciliar.
export const useContaCilSaldosConta = (empresaId: number | null | undefined, conta: string) =>
  useQuery({
    queryKey: ['contacil', 'saldos-conta', empresaId ?? null, conta] as const,
    enabled: empresaId != null && !!conta,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<IContaCilSaldoMes[]> => {
      const res = await api.get<IApiEnvelope<IContaCilSaldoMes[]>>(
        '/api/v1/contacil/dashboard/saldos-conta',
        { params: { empresa_id: empresaId, conta } },
      );
      return res.data.data;
    },
  });
